<template>
    <v-container>
        <v-alert type="info" class="body-2" v-show="suppliers.length === 0">
            No external suppliers defined.
        </v-alert>

        <title-card title="External suppliers"
                    help="external-supplier-project-entry-links"
                    v-show="suppliers.length > 0">

            <v-select
                v-model="suppliersSelectModel"
                :items="suppliers"
                solo
                item-text="name"
                item-value="id"
                chips
                label="Click here to select suppliers"
                multiple
                hide-details
                @change="suppliersSelected"
            >
                <template v-slot:selection="{ item, index }">
                    <v-chip color="primary">
                        {{ item.name }}
                    </v-chip>
                </template>
            </v-select>

        </title-card>

        <div class="mt-2" v-for="supplierLink in supplierLinks" :key="supplierLink.uuid">

            <supplier-link-card :supplier-link="supplierLink"
                                :project="project"
                                @change="supplierLinkChanged"
            ></supplier-link-card>

        </div>

        <json v-if="false" :data="supplierLinks"/>

    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import {mapState, mapActions} from 'vuex';
import TitleCard from "../../../../../components/UI/TitleCard";
import {v4 as uuid} from 'uuid';
import SupplierLinkCard from "../SupplierLinkCard";

const vuexModule = 'ProjectManager';

export default {

    components: {
        SupplierLinkCard,
        TitleCard,
        Json
    },

    props: {
        project: {
            type: Object,
            required: true,
        },
        selectedSubPanels: {
            type: Array,
            required: true,
        },
        subPanels: {
            type: Array | null,
            required: true,
        },
        errorMessages: {
            required: false,
            type: Object,
            default: () => {
                return {
                    name: []
                }
            }
        },
    },

    computed: {

        ...mapState('ExternalSuppliers', [
            'suppliers'
        ]),

        availableSubPanels() {
            return this.subPanels;
        }
    },

    watch: {
        project() {
            this.supplierLinks = [];
            this.createSuppliers();
        }
    },

    data() {
        return {
            supplierLinks: [],
            suppliersSelectModel: null,
        }
    },

    mounted() {
        this.getAllSuppliers()
            .then(() => {
                this.createSuppliers();
            })
            .catch(error => {
                console.log(error);
                Bus.$emit('flash-message', {type: 'error', message: "Failed loading suppliers!"});
            })
    },

    methods: {

        ...mapActions('ExternalSuppliers', [
            'getAllSuppliers',
        ]),

        ...mapActions(vuexModule, []),

        supplierLinkChanged(change) {

            let found = this.supplierLinks.find(sl => sl.uuid === change.supplierLink.uuid);

            if (found) {
                found = Object.assign(found, change.data);
                this.emit();
            }
        },

        suppliersSelected(supplierIds) {

            supplierIds.forEach(id => {
                const supplier = this.suppliers.find(s => s.id === id);
                this.createNewSupplierLinks(supplier);
            });

            this.supplierLinks = this.supplierLinks.filter(link => supplierIds.includes(link.supplierId));

            this.emit();
        },

        createSuppliers() {

            if (this.suppliers && this.project.project_suppliers) {

                let supplierIds = [];

                this.project.project_suppliers.forEach(supplier => {

                    if (!supplierIds.includes(supplier.supplier_id)) supplierIds.push(supplier.supplier_id);

                    const supplierModel = this.suppliers.find(s => s.id === supplier.supplier_id);
                    const query = supplierModel.entry_link.substring(supplierModel.entry_link.indexOf('?') + 1);
                    const subPanel = this.availableSubPanels.find(subPanel => subPanel.id === supplier.sub_panel_id);
                    const percent = supplier.max_completes === 0 ? 0 : Math.round(supplier.completed_count / supplier.max_completes * 100);

                    if (subPanel) {

                        const locale = subPanel.locales.find(locale => locale.id === supplier.locale_id);

                        if (locale) {

                            const entryLink = {
                                id: supplier.id,
                                enabled: true,
                                max_completes: supplier.max_completes,
                                completedCount: supplier.completed_count,
                                progressPercent: percent,
                                supplierId: supplierModel.id,
                                supplierName: supplierModel.name,
                                subPanelId: supplier.sub_panel_id,
                                subPanelName: subPanel.name,
                                localeId: locale.id,
                                localeCode: locale.code,
                                localeImage: locale.image,
                                uuid: supplier.uuid,
                                url: window.location.protocol + '//' + window.location.hostname + '/supplier/' + supplier.uuid + '?' + query
                            };

                            this.supplierLinks.push(entryLink)
                        }
                    }
                });

                this.suppliersSelectModel = supplierIds;
            }
        },

        supplierLinkExists(supplierId, subPanelId, localeId) {
            return !!this.supplierLinks.find(s => {
                return s.supplierId === supplierId &&
                    s.subPanelId === subPanelId &&
                    s.localeId === localeId;
            });
        },

        createNewSupplierLinks(supplier) {

            let combinations = 0;

            this.availableSubPanels.forEach(subPanel => {
                subPanel.locales.forEach(() => {
                    combinations++;
                });
            });

            this.availableSubPanels.forEach(subPanel => {

                subPanel.locales.forEach(locale => {

                    if (!this.supplierLinkExists(supplier.id, subPanel.id, locale.id)) {

                        const maxCompletes = Math.round(this.project.target_completes / combinations);
                        const query = supplier.entry_link.substring(supplier.entry_link.indexOf('?') + 1);
                        const unique = uuid();
                        const progressPercent = 0;

                        const entryLink = {
                            enabled: true,
                            max_completes: maxCompletes,
                            completedCount: 0,
                            progressPercent: progressPercent,
                            supplierId: supplier.id,
                            supplierName: supplier.name,
                            subPanelId: subPanel.id,
                            subPanelName: subPanel.name,
                            localeId: locale.id,
                            localeCode: locale.code,
                            localeImage: locale.image,
                            uuid: unique,
                            url: window.location.protocol + '//' + window.location.hostname + '/supplier/' + unique + '?' + query
                        };

                        this.supplierLinks.push(entryLink)
                    }
                })
            });
        },

        emit() {

            const mapped = this.supplierLinks.map(link => {
                return {
                    enabled: link.enabled,
                    uuid: link.uuid,
                    supplier_id: link.supplierId,
                    sub_panel_id: link.subPanelId,
                    locale_id: link.localeId,
                    max_completes: link.max_completes
                }
            })

            this.$emit('change', {project_suppliers: mapped});
        }
    }
}
</script>
